/* eslint-disable react/prop-types */
import * as React from 'react';

import { useRequest } from 'ahooks';
import { Button, Flex, notification, Typography, Upload } from 'antd';
import ImgCrop from 'antd-img-crop';

import { FileImageOutlinedIcon, UploadOutlinedIcon } from 'components';
import { getIdentityToken } from 'lib';
import type {
  AntdUploadChangeParam,
  FileInfo,
  FileOwnerType,
  ResponseErrors,
} from 'types';
import {
  cn,
  FILE,
  generateRequestUrl,
  generateResponseError,
  PATHNAME,
  STORAGE,
  validateMediaFile,
} from 'utils';

const {
  HEADER: {
    DEFAULT: { AUTHORIZATION: DEFAULT_AUTHORIZATION },
  },
} = STORAGE;
const {
  QUERY_PARAM: {
    DEFAULT: { COMMA: DEFAULT_COMMA, DOT: DEFAULT_DOT },
  },
} = PATHNAME;
const {
  FORMAT: {
    PICTURES: PICTURES_FORMAT,
    VIDEOS: VIDEOS_FORMAT,
    AUDIOS: AUDIOS_FORMAT,
    DOCUMENTS: DOCUMENTS_FORMAT,
    SHEET: SHEET_FORMAT,
  },
} = FILE;

type UploadVariant = 'default' | 'dragger';
type UploadConstraints = Partial<
  Record<'ownerType', FileOwnerType> &
    Record<'maxSize', number> &
    Record<
      | 'withPictures'
      | 'withVideos'
      | 'withAudios'
      | 'withDocuments'
      | 'withSheet',
      boolean
    >
>;

type UploadProps<T = FileInfo> = React.ComponentPropsWithoutRef<
  typeof Upload<T>
>;
type UploadDraggerProps = React.ComponentPropsWithoutRef<typeof Upload.Dragger>;

type UploadConfig<T = FileInfo> =
  | (UploadProps<T> &
      Partial<Record<'variant', Extract<UploadVariant, 'default'>>>)
  | (UploadDraggerProps &
      Partial<Record<'variant', Extract<UploadVariant, 'dragger'>>>);
type UploadConfigWithConstraints<T = FileInfo> = UploadConfig<T> &
  UploadConstraints;

const useUpload = ({
  variant = 'default',
  ownerType,
  maxSize,
  withPictures,
  withVideos,
  withAudios,
  withDocuments,
  withSheet,
  accept,
  action,
  headers,
  className,
  children,
  beforeUpload,
  onChange,
  ...props
}: UploadConfigWithConstraints): StrictOmit<UploadConfig, 'variant'> => {
  const { data } = useRequest(getIdentityToken);

  const { tokenType, accessToken } = data ?? {};
  const isDefaultVariant = variant === 'default';
  const isDraggerVariant = variant === 'dragger';

  const allowedFormats = React.useMemo<ReadonlyArray<string>>(() => {
    const formats: string[] = [];

    if (withPictures) formats.push(...PICTURES_FORMAT);
    if (withVideos) formats.push(...VIDEOS_FORMAT);
    if (withAudios) formats.push(...AUDIOS_FORMAT);
    if (withDocuments) formats.push(...DOCUMENTS_FORMAT);
    if (withSheet) formats.push(...SHEET_FORMAT);

    return formats;
  }, [withPictures, withVideos, withAudios, withSheet, withDocuments]);

  const acceptString = React.useMemo<typeof accept>(() => {
    if (accept) return accept;

    return allowedFormats.length
      ? allowedFormats
          .map(format => `${DEFAULT_DOT}${format}`)
          .join(DEFAULT_COMMA)
      : undefined;
  }, [allowedFormats, accept]);

  const processedAction = React.useMemo<typeof action>(() => {
    if (action) return action;
    if (!ownerType) return undefined;

    return generateRequestUrl(`file/uploadFile/${ownerType}`);
  }, [action, ownerType]);

  const computedChildren = React.useMemo<typeof children>(() => {
    if (children) return children;

    if (isDraggerVariant) {
      return (
        <React.Fragment>
          <FileImageOutlinedIcon className='text-2xl text-[--ant-color-link-hover]' />
          <Typography.Title level={5} className='mb-2.5 mt-2 font-medium'>
            بارگذاری مستندات
          </Typography.Title>
          <Flex
            vertical
            gap={2}
            justify='center'
            align='center'
            className='mt-1 text-gray-400'
          >
            {withPictures ? (
              <p>
                <span>فرمت قابل قبول تصویر: </span>
                <span>{PICTURES_FORMAT.join(`${DEFAULT_COMMA} `)}</span>
              </p>
            ) : null}
            {withDocuments ? (
              <p>
                <span>فرمت قابل قبول فایل‌: </span>
                <span>{DOCUMENTS_FORMAT.join(`${DEFAULT_COMMA} `)}</span>
              </p>
            ) : null}
            {withVideos ? (
              <p>
                <span>فرمت قابل قبول ویدیو: </span>
                <span>{VIDEOS_FORMAT.join(`${DEFAULT_COMMA} `)}</span>
              </p>
            ) : null}
            {withAudios ? (
              <p>
                <span>فرمت قابل قبول صدا: </span>
                <span>{AUDIOS_FORMAT.join(`${DEFAULT_COMMA} `)}</span>
              </p>
            ) : null}
            {withSheet ? (
              <p>
                <span>فرمت قابل قبول گزارش: </span>
                <span>{SHEET_FORMAT.join(`${DEFAULT_COMMA} `)}</span>
              </p>
            ) : null}
          </Flex>
        </React.Fragment>
      );
    }

    return <Button icon={<UploadOutlinedIcon />}>بارگذاری</Button>;
  }, [
    isDraggerVariant,
    withPictures,
    withVideos,
    withAudios,
    withSheet,
    withDocuments,
    children,
  ]);

  return {
    accept: acceptString,
    action: processedAction,
    headers: {
      [DEFAULT_AUTHORIZATION]: `${tokenType} ${accessToken}`,
      ...headers,
    },
    className: cn(
      isDefaultVariant &&
        '[&_.ant-upload-list-item-container+.ant-upload-select]:hidden [&_.ant-upload-list-item-container]:size-full [&_.ant-upload-list-item-thumbnail]:h-[inherit] [&_.ant-upload-list-item-thumbnail]:rounded-sm [&_.ant-upload-list-item-thumbnail]:py-0.5 [&_.ant-upload-list-item]:mt-0 [&_.ant-upload-list]:mt-0 [&_.ant-upload-list]:w-full [&_.ant-upload-select:has(+.ant-upload-list)]:hidden [&_.ant-upload-select]:size-full [&_.ant-upload]:size-full [&_.ant-upload]:has-[.ant-upload-list:empty]:grid',
      isDraggerVariant && 'min-h-48',
      'grid [&_*]:border-solid',
      className,
    ),
    children: computedChildren,
    beforeUpload: (...params) => {
      beforeUpload?.(...params);

      const [{ name, size }] = params;
      const isValidFormat = validateMediaFile(name, allowedFormats);
      const isSizeLimited = maxSize ? size <= maxSize : true;
      const isAcceptableFile = isValidFormat && isSizeLimited;

      return isAcceptableFile || Upload.LIST_IGNORE;
    },
    onChange: (info: AntdUploadChangeParam) => {
      onChange?.(info);

      const {
        file: { status, response },
      } = info;
      const { errors } = (response as any) ?? {};

      if (status === 'error' && errors) {
        notification.error({
          message: generateResponseError(response as unknown as ResponseErrors),
        });
      }
    },
    ...props,
  };
};

type AntdUploadProps = UploadProps & UploadConstraints;

const AntdUpload = React.forwardRef<
  React.ElementRef<typeof Upload<FileInfo>>,
  AntdUploadProps
>(
  (
    { withPictures = true, listType = 'picture', maxCount = 1, ...props },
    ref,
  ) => {
    const upload = useUpload({
      withPictures,
      listType,
      maxCount,
      ...props,
    });

    return <Upload<FileInfo> ref={ref} {...upload} />;
  },
);
AntdUpload.displayName = 'AntdUpload';

type AntdUploadDraggerProps = UploadDraggerProps & UploadConstraints;

const AntdUploadDragger = React.forwardRef<
  React.ElementRef<typeof Upload.Dragger>,
  AntdUploadDraggerProps
>(
  (
    {
      withPictures = true,
      withVideos = true,
      withDocuments = true,
      multiple = true,
      showUploadList = false,
      ...props
    },
    ref,
  ) => {
    const upload = useUpload({
      variant: 'dragger',
      withPictures,
      withVideos,
      withDocuments,
      multiple,
      showUploadList,
      ...props,
    });

    return <Upload.Dragger ref={ref} {...upload} />;
  },
);
AntdUploadDragger.displayName = 'AntdUploadDragger';

type AntdUploadImgCropProps = Partial<
  Record<
    'rootConfig',
    StrictOmit<React.ComponentPropsWithoutRef<typeof ImgCrop>, 'children'>
  >
> &
  Pick<UploadConstraints, 'ownerType' | 'maxSize'> &
  UploadProps;

const AntdUploadImgCrop = React.forwardRef<
  React.ElementRef<typeof Upload<FileInfo>>,
  AntdUploadImgCropProps
>(({ rootConfig, listType = 'picture', ...props }, ref) => {
  const upload = useUpload({
    withPictures: true,
    listType,
    ...props,
  });

  const {
    modalTitle = 'ویرایشگر',
    resetText = 'بازنشانی',
    fillColor = 'transparent',
    cropShape = 'rect',
    rotationSlider = true,
    aspectSlider = true,
    zoomSlider = true,
    showReset = true,
    showGrid = true,
    minZoom = 0,
    maxZoom = 4,
    quality = 1,
    modalClassName,
    ...otherConfig
  } = rootConfig ?? {};

  return (
    <ImgCrop
      modalTitle={modalTitle}
      resetText={resetText}
      fillColor={fillColor}
      cropShape={cropShape}
      rotationSlider={rotationSlider}
      aspectSlider={aspectSlider}
      zoomSlider={zoomSlider}
      showReset={showReset}
      showGrid={showGrid}
      minZoom={minZoom}
      maxZoom={maxZoom}
      quality={quality}
      modalClassName={cn(
        '[&_.img-crop-control+.ant-btn]:absolute',
        modalClassName,
      )}
      {...otherConfig}
    >
      <Upload<FileInfo> ref={ref} {...upload} />
    </ImgCrop>
  );
});
AntdUploadImgCrop.displayName = 'AntdUploadImgCrop';

export { AntdUpload, AntdUploadDragger, AntdUploadImgCrop };
export { useUpload };
